import { Observer } from "mobx-react-lite";
import { FunctionComponent, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Outlet, useMatch } from "react-router-dom";

import {
  applyOpacityToHexColor,
  mergeStyles,
  Stack,
  useTheme
} from "@bps/fluent-ui";
import { useGoogleAnalyticsPageTracking } from "@libs/analytics/google/index.tsx";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { routes } from "@libs/routing/routes.ts";
import { QuickLinks } from "@modules/clinical/screens/quick-links/QuickLinks.tsx";
import { useStores } from "@stores/hooks/useStores.ts";
import { When } from "@ui-components/withPerm.tsx";

import { ServiceWorkerRegistration } from "../../ServiceWorkerRegistration.tsx";
import { NavBar } from "../NavBar/NavBar.tsx";
import { InactivityPinDialog } from "../session/components/InactivityPinDialogForm/InactivityPinDialog.tsx";
import { useTitle } from "../session/hooks/useTitle.tsx";
import { AccCertCheck } from "./AccCertCheck.tsx";
import { AccEnvMessageBar } from "./AccEnvMessageBar.tsx";
import { Dialogs } from "./Dialogs.tsx";
import { ErrorBoundaryFallback } from "./ErrorBoundaryFallback.tsx";
import { Messages } from "./Messages.tsx";

export const MainLayout: FunctionComponent = () => {
  const theme = useTheme();
  const { userExperience } = useStores();
  const isClinical = useMatch({
    path: routes.records.basePath.pattern,
    end: false
  });

  useGoogleAnalyticsPageTracking();
  useTitle();

  const backgroundColor = useMemo(() => {
    if (!isClinical) return theme.semanticColors.disabledBackground;
    return applyOpacityToHexColor(theme.palette.neutralTertiary, 40);
  }, [
    isClinical,
    theme.palette.neutralTertiary,
    theme.semanticColors.disabledBackground
  ]);

  const mainStyles = mergeStyles({
    flexGrow: 1,
    backgroundColor,
    overflow: "auto",
    padding: "8px 16px",
    position: "relative"
  });

  return (
    <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
      <Stack verticalFill>
        <ServiceWorkerRegistration />
        <When permission={Permission.InactiveLockScreenAllowed}>
          <InactivityPinDialog />
        </When>
        <NavBar />
        <AccEnvMessageBar />
        <AccCertCheck />
        <Observer>
          {() => <>{!userExperience.quickLinksOnBottom && <QuickLinks />}</>}
        </Observer>
        <Messages />
        <main className={mainStyles}>
          <Outlet />
          <Dialogs />
        </main>

        <Observer>
          {() => <>{userExperience.quickLinksOnBottom && <QuickLinks />}</>}
        </Observer>
      </Stack>
    </ErrorBoundary>
  );
};
